/**
 * Firestore Service — THE single data layer.
 * Reads and writes go directly to Firestore; security rules enforce
 * public reads and admin-only writes (`admin: true` custom claim).
 * Mirrors the semantics of the former Flask/Firestore repositories:
 *   - projects      → order by created_at desc
 *   - achievements  → order by date desc
 *   - hackathons    → order by date desc
 *   - certificates  → order by issue_date desc
 *   - skills        → group by category, order by category asc, name asc
 *   - timeline      → order by sort_date desc, order asc
 *   - owner         → meta/owner document with default-profile fallback
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const nowIso = () => new Date().toISOString();

// ── Owner ─────────────────────────────────────────────────

export const DEFAULT_OWNER_PROFILE = {
  name: 'Your Name',
  title: 'Software Developer',
  email: 'you@example.com',
  github: 'https://github.com/yourusername',
  linkedin: 'https://linkedin.com/in/yourusername',
  bio: 'Passionate software developer building web apps, games, and tools.',
  resume_url: '',
  resume_storage_url: '',
  resume_path: '',
  resume_name: '',
  resume_updated_at: '',
  resume_history: [],
};

export async function getOwner() {
  try {
    const snapshot = await getDoc(doc(db, 'meta', 'owner'));
    if (!snapshot.exists()) return { ...DEFAULT_OWNER_PROFILE };

    const data = snapshot.data() || {};
    const history = Array.isArray(data.resume_history)
      ? data.resume_history
      : [];

    return {
      ...DEFAULT_OWNER_PROFILE,
      name: data.name ?? DEFAULT_OWNER_PROFILE.name,
      title: data.title ?? DEFAULT_OWNER_PROFILE.title,
      email: data.email ?? DEFAULT_OWNER_PROFILE.email,
      github: data.github ?? DEFAULT_OWNER_PROFILE.github,
      linkedin: data.linkedin ?? DEFAULT_OWNER_PROFILE.linkedin,
      bio: data.bio ?? DEFAULT_OWNER_PROFILE.bio,
      resume_url: data.resume_url ?? '',
      resume_storage_url: data.resume_storage_url ?? '',
      resume_path: data.resume_path ?? '',
      resume_name: data.resume_name ?? '',
      resume_updated_at: data.resume_updated_at ?? '',
      resume_history: history,
    };
  } catch (error) {
    console.error('Error fetching owner:', error);
    throw error;
  }
}

export async function upsertOwner(data) {
  try {
    const history = Array.isArray(data.resume_history)
      ? data.resume_history
      : [];

    const payload = {
      name: data.name ?? DEFAULT_OWNER_PROFILE.name,
      title: data.title ?? DEFAULT_OWNER_PROFILE.title,
      email: data.email ?? DEFAULT_OWNER_PROFILE.email,
      github: data.github ?? DEFAULT_OWNER_PROFILE.github,
      linkedin: data.linkedin ?? DEFAULT_OWNER_PROFILE.linkedin,
      bio: data.bio ?? DEFAULT_OWNER_PROFILE.bio,
      resume_url: data.resume_url ?? '',
      resume_storage_url: data.resume_storage_url ?? '',
      resume_path: data.resume_path ?? '',
      resume_name: data.resume_name ?? '',
      resume_updated_at: data.resume_updated_at ?? '',
      resume_history: history,
      updated_at: nowIso(),
    };

    await setDoc(doc(db, 'meta', 'owner'), payload, { merge: true });
    return getOwner();
  } catch (error) {
    console.error('Error saving owner:', error);
    throw error;
  }
}

// ── Projects ──────────────────────────────────────────────

export async function getProjects(filters = {}) {
  try {
    const constraints = [];

    if (filters.category) constraints.push(where('category', '==', filters.category));
    if (filters.featured !== undefined) constraints.push(where('featured', '==', filters.featured));

    constraints.push(orderBy('created_at', 'desc'));
    const snapshot = await getDocs(query(collection(db, 'projects'), ...constraints));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
}

export async function getProject(id) {
  try {
    const snapshot = await getDoc(doc(db, 'projects', id));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
}

async function createEntity(collectionName, data) {
  const docRef = await addDoc(collection(db, collectionName), {
    ...data,
    created_at: nowIso(),
  });
  return { id: docRef.id, ...data };
}

async function updateEntity(collectionName, id, data) {
  await updateDoc(doc(db, collectionName, id), data);
  return { id, ...data };
}

async function deleteEntity(collectionName, id) {
  await deleteDoc(doc(db, collectionName, id));
  return true;
}

export const createProject = (data) => wrap(createEntity('projects', data), 'creating project');
export const updateProject = (id, data) => wrap(updateEntity('projects', id, data), 'updating project');
export const deleteProject = (id) => wrap(deleteEntity('projects', id), 'deleting project');

// ── Skills ────────────────────────────────────────────────

export async function getSkills() {
  try {
    const q = query(
      collection(db, 'skills'),
      orderBy('category'),
      orderBy('name')
    );
    const snapshot = await getDocs(q);
    const grouped = {};
    snapshot.docs.forEach(d => {
      const skill = { id: d.id, ...d.data() };
      const cat = skill.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(skill);
    });
    return grouped;
  } catch (error) {
    console.error('Error fetching skills:', error);
    throw error;
  }
}

export const getSkill = (id) => wrap(getEntity('skills', id), 'fetching skill');
export const createSkill = (data) => wrap(createEntity('skills', data), 'creating skill');
export const updateSkill = (id, data) => wrap(updateEntity('skills', id, data), 'updating skill');
export const deleteSkill = (id) => wrap(deleteEntity('skills', id), 'deleting skill');

// ── Achievements ──────────────────────────────────────────

export async function getAchievements() {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'achievements'), orderBy('date', 'desc'))
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching achievements:', error);
    throw error;
  }
}

export const getAchievement = (id) => wrap(getEntity('achievements', id), 'fetching achievement');
export const createAchievement = (data) => wrap(createEntity('achievements', data), 'creating achievement');
export const updateAchievement = (id, data) => wrap(updateEntity('achievements', id, data), 'updating achievement');
export const deleteAchievement = (id) => wrap(deleteEntity('achievements', id), 'deleting achievement');

// ── Hackathons ────────────────────────────────────────────

export async function getHackathons() {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'hackathons'), orderBy('date', 'desc'))
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching hackathons:', error);
    throw error;
  }
}

export const getHackathon = (id) => wrap(getEntity('hackathons', id), 'fetching hackathon');
export const createHackathon = (data) => wrap(createEntity('hackathons', data), 'creating hackathon');
export const updateHackathon = (id, data) => wrap(updateEntity('hackathons', id, data), 'updating hackathon');
export const deleteHackathon = (id) => wrap(deleteEntity('hackathons', id), 'deleting hackathon');

// ── Certificates ──────────────────────────────────────────

export async function getCertificates() {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'certificates'), orderBy('issue_date', 'desc'))
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching certificates:', error);
    throw error;
  }
}

export const getCertificate = (id) => wrap(getEntity('certificates', id), 'fetching certificate');
export const createCertificate = (data) => wrap(createEntity('certificates', data), 'creating certificate');
export const updateCertificate = (id, data) => wrap(updateEntity('certificates', id, data), 'updating certificate');
export const deleteCertificate = (id) => wrap(deleteEntity('certificates', id), 'deleting certificate');

// ── Timeline ──────────────────────────────────────────────

/**
 * Normalize a flexible date string to YYYY-MM-DD for proper sorting.
 * "2025"     → "2025-12-31" (end of year)
 * "2025-01"  → "2025-01-31" (end of month)
 * "2025-01-15" → unchanged
 * Returns null for empty/invalid dates.
 */
function normalizeDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const parts = dateStr.split('-');
  try {
    if (parts.length === 3) return dateStr;
    if (parts.length === 2) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) return null;
      const lastDay = month === 12 ? 31 : new Date(year, month, 0).getDate();
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    if (parts.length === 1 && parts[0]) {
      const year = parseInt(parts[0], 10);
      if (Number.isNaN(year)) return null;
      return `${String(year).padStart(4, '0')}-12-31`;
    }
  } catch {
    // fall through
  }
  return null;
}

export async function getTimeline() {
  try {
    const q = query(
      collection(db, 'timeline'),
      orderBy('sort_date', 'desc'),
      orderBy('order', 'asc')
    );
    const snapshot = await getDocs(q);

    const UNDATED = '9999-12-31';
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const dateA = a.sort_date || UNDATED;
        const dateB = b.sort_date || UNDATED;
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return (a.order ?? 0) - (b.order ?? 0);
      });
  } catch (error) {
    console.error('Error fetching timeline:', error);
    throw error;
  }
}

export async function createTimeline(data) {
  try {
    const sortDate = normalizeDate(data.date);
    const payload = {
      title: data.title,
      description: data.description,
      date: data.date,
      sort_date: sortDate,
      order: data.order ?? 0,
      created_at: nowIso(),
    };
    const docRef = await addDoc(collection(db, 'timeline'), payload);
    return { id: docRef.id, ...payload };
  } catch (error) {
    console.error('Error creating timeline entry:', error);
    throw error;
  }
}

export async function updateTimeline(id, data) {
  try {
    const allowed = ['title', 'description', 'date', 'order'];
    const payload = {};
    allowed.forEach(key => {
      if (data[key] !== undefined && data[key] !== null) payload[key] = data[key];
    });

    // Recalculate sort_date if date was updated
    if ('date' in payload) payload.sort_date = normalizeDate(payload.date);

    await updateDoc(doc(db, 'timeline', id), payload);
    return { id, ...payload };
  } catch (error) {
    console.error('Error updating timeline entry:', error);
    throw error;
  }
}

export const deleteTimeline = (id) => wrap(deleteEntity('timeline', id), 'deleting timeline entry');

// ── Blog ─────────────────────────────────────────────────

export async function getBlogPosts() {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'blog'), orderBy('date', 'desc'))
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
}

export async function getBlogPost(slug) {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'blog'), where('slug', '==', slug))
    );
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
}

export const createBlogPost = (data) => wrap(createEntity('blog', data), 'creating blog post');
export const updateBlogPost = (id, data) => wrap(updateEntity('blog', id, data), 'updating blog post');
export const deleteBlogPost = (id) => wrap(deleteEntity('blog', id), 'deleting blog post');

// ── Shared helpers ────────────────────────────────────────

async function getEntity(collectionName, id) {
  const snapshot = await getDoc(doc(db, collectionName, id));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

function wrap(promise, action) {
  return promise.catch(error => {
    console.error(`Error ${action}:`, error);
    throw error;
  });
}

// Exported for testing
export { normalizeDate };
